import React from 'react';
import { useSearchParams } from 'react-router-dom';
import type {
  AdminDemoLeadListQuery,
  AdminDocumentPackageSearchQuery,
} from '../legalTypes';

const readPositiveInt = (value: string | null): number | undefined => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

const useQueryWriter = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const setQueryValue = React.useCallback((key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === '') {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next, { replace: false });
  }, [searchParams, setSearchParams]);
  const setListQueryValue = React.useCallback((key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === '') {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    next.delete('page');
    setSearchParams(next, { replace: false });
  }, [searchParams, setSearchParams]);
  return { searchParams, setQueryValue, setListQueryValue, setSearchParams };
};

const useDebouncedQueryText = (
  urlValue: string,
  commit: (value: string) => void
): [string, React.Dispatch<React.SetStateAction<string>>] => {
  const [value, setValue] = React.useState(urlValue);
  React.useEffect(() => setValue(urlValue), [urlValue]);
  React.useEffect(() => {
    if (value === urlValue) {
      return undefined;
    }
    const timer = window.setTimeout(() => commit(value), 180);
    return () => window.clearTimeout(timer);
  }, [commit, urlValue, value]);
  return [value, setValue];
};

export const useDemoLeadRouteQuery = () => {
  const { searchParams, setQueryValue, setListQueryValue } = useQueryWriter();
  const urlSearch = searchParams.get('q') ?? '';
  const commitSearch = React.useCallback(
    (value: string) => setListQueryValue('q', value),
    [setListQueryValue]
  );
  const [searchText, setSearchText] = useDebouncedQueryText(urlSearch, commitSearch);
  const status = searchParams.get('status') ?? '';
  const kind = searchParams.get('kind') ?? '';
  const selectedLeadId = readPositiveInt(searchParams.get('lead')) ?? null;
  const page = readPositiveInt(searchParams.get('page')) ?? 1;
  const query = React.useMemo<AdminDemoLeadListQuery>(() => ({
    page,
    pageSize: 25,
    q: urlSearch || undefined,
    status: status || undefined,
    kind: kind || undefined,
  }), [kind, page, status, urlSearch]);
  return {
    query,
    searchText,
    setSearchText,
    status,
    kind,
    selectedLeadId,
    setStatus: (value: string) => setListQueryValue('status', value),
    setKind: (value: string) => setListQueryValue('kind', value),
    setSelectedLead: (leadId: number | null) => setQueryValue('lead', leadId?.toString() ?? null),
    setPage: (nextPage: number) => setQueryValue('page', nextPage > 1 ? nextPage.toString() : null),
  };
};

export const useDocumentRouteQuery = () => {
  const { searchParams, setQueryValue, setListQueryValue, setSearchParams } = useQueryWriter();
  const urlSearch = searchParams.get('q') ?? '';
  const commitSearch = React.useCallback(
    (value: string) => setListQueryValue('q', value),
    [setListQueryValue]
  );
  const [searchText, setSearchText] = useDebouncedQueryText(urlSearch, commitSearch);
  const value = React.useCallback((key: string) => searchParams.get(key) ?? '', [searchParams]);
  const page = readPositiveInt(searchParams.get('page')) ?? 1;
  const companyId = readPositiveInt(searchParams.get('company'));
  const purpose = value('purpose');
  const jurisdiction = value('jurisdiction');
  const assignee = value('assignee');
  const state = value('state');
  const updatedFrom = value('updatedFrom');
  const signatureStatus = value('signatureStatus');
  const missingInput = value('missingInput') === 'true';
  const generationFailure = value('generationFailure') === 'true';
  const archived = value('archived') === 'true';
  const query = React.useMemo<AdminDocumentPackageSearchQuery>(() => ({
    page,
    pageSize: 25,
    q: urlSearch || undefined,
    companyId,
    purpose: purpose || undefined,
    jurisdiction: jurisdiction || undefined,
    assignee: assignee || undefined,
    state: state || undefined,
    updatedFrom: updatedFrom || undefined,
    missingInput: missingInput || undefined,
    generationFailure: generationFailure || undefined,
    signatureStatus: signatureStatus || undefined,
    archived: archived || undefined,
  }), [
    archived,
    assignee,
    companyId,
    generationFailure,
    jurisdiction,
    missingInput,
    page,
    purpose,
    signatureStatus,
    state,
    updatedFrom,
    urlSearch,
  ]);
  const setSelectedPackage = React.useCallback((
    packageId: string | null,
    selectedCompanyId?: number | null
  ) => {
    const next = new URLSearchParams(searchParams);
    if (packageId) {
      next.set('package', packageId);
    } else {
      next.delete('package');
    }
    if (selectedCompanyId) {
      next.set('company', selectedCompanyId.toString());
    }
    setSearchParams(next, { replace: false });
  }, [searchParams, setSearchParams]);
  return {
    query,
    searchText,
    setSearchText,
    selectedPackageId: value('package') || null,
    purpose,
    jurisdiction,
    assignee,
    state,
    updatedFrom,
    signatureStatus,
    missingInput,
    generationFailure,
    archived,
    setFilter: (key: string, filterValue: string | boolean) => setListQueryValue(
      key,
      typeof filterValue === 'boolean' ? (filterValue ? 'true' : null) : filterValue
    ),
    setPage: (nextPage: number) => setQueryValue('page', nextPage > 1 ? nextPage.toString() : null),
    setSelectedPackage,
  };
};
